import { type AssetLevelInputType } from '../types/input-steps.types';
import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  IconButton,
  FormControl,
  Checkbox,
  Tooltip,
} from '@mui/material';
import { Add, Delete, Warning } from '@mui/icons-material';
import { CustomTextField, CustomTimePicker } from './CustomInput';
import useAppTheme from 'hooks/useAppTheme';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { CustomerShiftsDetails } from 'utils/constants/customerShiftDetails';
import { useAppSelector } from 'store/hook';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useTranslation } from 'react-i18next';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const useTableHeaders = () => {
  const { t } = useTranslation();
  return [
    { label: '', align: 'center' as const },
    { label: t('cdp_table_header_asset'), align: 'left' as const },
    { label: t('cdp_table_header_last_location'), align: 'left' as const },
    { label: t('cdp_table_header_break_time'), align: 'center' as const },
    { label: t('cdp_break_minutes_label'), align: 'center' as const },
    { label: t('cdp_table_header_capacity_tons'), align: 'center' as const },
    { label: '', align: 'right' as const },
  ];
};

export const AssetLevelInput = ({
  assets,
  errors,
  allAssets,
  onChangeAsset,
  onDelete,
  setAssets,
  selectedShiftNumber,
  setSelectedShiftNumber,
  selectedAssetIds,
  onToggleAsset,
}: {
  assets: AssetLevelInputType[];
  errors: Record<string, Record<string, string>>;
  allAssets: Array<AssetLevelInputType & { isActive: boolean }>;
  onChangeAsset: (
    id: string,
    field: 'capacity' | 'lunchStart' | 'lunchEnd' | 'lunchBreakMinutes',
    value: string | null
  ) => void;
  onDelete: (id: string) => void;
  setAssets: React.Dispatch<React.SetStateAction<AssetLevelInputType[]>>;
  selectedShiftNumber: number | null;
  setSelectedShiftNumber: React.Dispatch<React.SetStateAction<number | null>>;
  selectedAssetIds: Set<string>;
  onToggleAsset: (id: string) => void;
}) => {
  const { t } = useTranslation();
  const tableHeaders = useTableHeaders();
  const { enableDarkTheme } = useAppTheme();
  const { customerCode, customerTz } = useCustomerInfo();

  const customerKey = (customerCode ?? '').toLowerCase();

  const shiftConfigData = useAppSelector(
    (state) => state.customerShiftConfigReducer.data
  );

  const shiftPeriods = useMemo(() => {
    if (shiftConfigData?.shiftPeriods?.length)
      return shiftConfigData.shiftPeriods;
    const customerShift =
      CustomerShiftsDetails[customerKey] ?? CustomerShiftsDetails.others;
    return customerShift.shifts.map((s, i) => ({
      shiftName: `Shift ${i + 1}`,
      startHour: s.startHour,
      endHour: s.endHour,
    }));
  }, [shiftConfigData, customerKey]);

  useEffect(() => {
    if (!selectedShiftNumber) return;

    const period = shiftPeriods[selectedShiftNumber - 1];
    if (!period) return;

    const todayInTz = dayjs().tz(customerTz);
    const todayDateStr = todayInTz.format('YYYY-MM-DD');

    const { startHour, endHour } = period;
    const normalizedEndHour = endHour <= startHour ? endHour + 24 : endHour;
    const customerDayStart = dayjs.tz(`${todayDateStr}T00:00:00`, customerTz);
    const startTime = customerDayStart.add(startHour, 'hour');
    const endTime = customerDayStart.add(normalizedEndHour, 'hour');

    const shiftDuration = endTime.diff(startTime, 'minutes');
    const lunchStart = startTime.add(shiftDuration / 2, 'minutes');
    const lunchEnd = lunchStart.add(60, 'minutes');

    setAssets((prevAssets) =>
      prevAssets.map((asset) => ({
        ...asset,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        lunchStart: lunchStart.toISOString(),
        lunchEnd: lunchEnd.toISOString(),
      }))
    );
  }, [selectedShiftNumber, shiftPeriods, customerTz, setAssets]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: `1px solid ${enableDarkTheme ? '#334155' : '#BDBDBD'}`,
        borderRadius: 2,
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 500, fontSize: '18px', color: 'text.primary' }}
        >
          {t('asset_level_input')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={selectedShiftNumber ?? 1}
              onChange={(e) => {
                setSelectedShiftNumber(Number(e.target.value));
              }}
              size="small"
              sx={{
                borderRadius: '8px',
                height: '36px',
                fontSize: '14px',
              }}
            >
              {shiftPeriods.map((period, index) => {
                const shiftNum = index + 1;
                const label = period.shiftName || `Shift ${shiftNum}`;
                const startLabel = `${String(period.startHour % 24).padStart(
                  2,
                  '0'
                )}:00`;
                const rawEnd = period.endHour;
                const endLabel = `${String(rawEnd % 24).padStart(2, '0')}:00${
                  rawEnd >= 24 ? '+1d' : ''
                }`;
                return (
                  <MenuItem key={shiftNum} value={shiftNum}>
                    {label}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <Box sx={{ width: '200px' }}>
            <FormControl fullWidth>
              <Select
                multiple
                fullWidth
                value={assets.map((asset) => asset.id)}
                onChange={(e) => {
                  const selectedIds = e.target.value as string[];
                  setAssets((prevAssets) => {
                    const keptAssets = prevAssets.filter((a) =>
                      selectedIds.includes(a.id)
                    );
                    const newAssetIds = selectedIds.filter(
                      (id) => !prevAssets.some((a) => a.id === id)
                    );
                    const newAssets = allAssets.filter((a) =>
                      newAssetIds.includes(a.id)
                    );
                    return [...keptAssets, ...newAssets];
                  });
                }}
                startAdornment={<Add />}
                displayEmpty
                size="small"
                renderValue={(selected) =>
                  selected.length === 0 ? (
                    <Typography
                      sx={{ color: 'text.secondary', fontSize: '14px' }}
                    >
                      {t('add_asset')}
                    </Typography>
                  ) : (
                    t('cdp_assets_selected', { count: selected.length })
                  )
                }
                MenuProps={{
                  sx: { maxHeight: '400px', maxWidth: '400px' },
                }}
                sx={{
                  borderRadius: '8px',
                  height: '36px',
                  fontSize: '14px',
                }}
              >
                {allAssets.map((asset) => (
                  <MenuItem key={asset.id} value={asset.id}>
                    {!asset.isActive ? (
                      <Tooltip arrow title={t('cdp_asset_out_of_service')}>
                        <Box display={'flex'} alignItems={'center'}>
                          <Checkbox
                            checked={assets.some((a) => a.id === asset.id)}
                          />
                          <Warning color="warning" />
                          <Typography
                            noWrap
                            color={'orange'}
                          >{`${asset.name}`}</Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      <React.Fragment>
                        <Checkbox
                          checked={assets.some((a) => a.id === asset.id)}
                        />
                        <Typography noWrap>{`${asset.name}`}</Typography>
                      </React.Fragment>
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      <TableContainer>
        <Table
          sx={{
            borderCollapse: 'collapse',
            '& td, & th': {
              borderBottom: enableDarkTheme
                ? '1px solid #2F445D'
                : '1px solid #9E9E9E',
              position: 'relative',
            },
            '& td:not(:last-child)::after, & th:not(:last-child)::after': {
              content: '""',
              position: 'absolute',
              top: '40%',
              bottom: '40%',
              right: 0,
              width: '2px',
              backgroundColor: enableDarkTheme ? '#2F445D' : '#9E9E9E',
            },
          }}
        >
          <TableHead>
            <TableRow>
              {tableHeaders.map(({ label, align }) => (
                <TableCell
                  key={label}
                  align={align}
                  sx={{ color: 'text.secondary', fontWeight: 600 }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {assets.map((asset) => (
              <TableRow key={asset.id}>
                <TableCell align="center" padding="checkbox">
                  <Checkbox
                    checked={selectedAssetIds.has(asset.id)}
                    onChange={() => {
                      onToggleAsset(asset.id);
                    }}
                    size="small"
                  />
                </TableCell>
                <TableCell align="left">
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '14px',
                      color: 'text.primary',
                    }}
                  >
                    {asset.name}
                  </Typography>
                  {asset.lastLocation ? (
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: 'text.secondary',
                        mt: 0.25,
                      }}
                    >
                      {asset.lastLocation}
                      {asset.lastLocationTs
                        ? ` · ${dayjs(asset.lastLocationTs).fromNow()}`
                        : ''}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell align="left">
                  {asset.lastLat != null && asset.lastLng != null ? (
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: 'text.primary',
                        fontWeight: 500,
                      }}
                    >
                      {asset.lastLat.toFixed(5)}, {asset.lastLng.toFixed(5)}
                    </Typography>
                  ) : (
                    <Typography
                      sx={{ fontSize: '12px', color: 'text.disabled' }}
                    >
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'inline-flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', gap: '2px', alignItems: 'center' }}
                    >
                      <CustomTimePicker
                        value={asset.lunchStart}
                        onChange={(val) => {
                          onChangeAsset(asset.id, 'lunchStart', val);
                        }}
                        error={errors[asset.id]?.lunchStart}
                      />
                      -
                      <CustomTimePicker
                        value={asset.lunchEnd}
                        onChange={(val) => {
                          onChangeAsset(asset.id, 'lunchEnd', val);
                        }}
                        error={errors[asset.id]?.lunchEnd}
                      />
                    </Box>
                    {errors[asset.id]?.lunchStart ||
                    errors[asset.id]?.lunchEnd ? (
                      <Typography
                        color="error"
                        sx={{
                          fontSize: '12px',
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {errors[asset.id]?.lunchStart ||
                          errors[asset.id]?.lunchEnd}
                      </Typography>
                    ) : null}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <CustomTextField
                    label=""
                    name="lunchBreakMinutes"
                    value={asset.lunchBreakMinutes ?? '60'}
                    onChange={(e) => {
                      onChangeAsset(
                        asset.id,
                        'lunchBreakMinutes',
                        e.target.value
                      );
                    }}
                    type="number"
                  />
                </TableCell>
                <TableCell align="center">
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'inline-flex',
                      flexDirection: 'column',
                    }}
                  >
                    <CustomTextField
                      label=""
                      name="capacity"
                      value={asset.capacity}
                      onChange={(e) => {
                        onChangeAsset(asset.id, 'capacity', e.target.value);
                      }}
                      error={errors[asset.id]?.capacity}
                    />

                    {errors[asset.id]?.capacity ? (
                      <Typography
                        color="error"
                        sx={{
                          fontSize: '12px',
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {errors[asset.id]?.capacity}
                      </Typography>
                    ) : null}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={() => {
                      onDelete(asset.id);
                    }}
                    sx={{
                      color: '#ef4444',
                      '&:hover': {
                        color: '#f87171',
                        bgcolor: 'rgba(239, 68, 68, 0.1)',
                      },
                    }}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};
